// @ts-strict-ignore
import * as React from "react";

import { SSE } from "sse.js";

import { toast } from "@needl-ai/common/components/ui/toaster";
import { getRequestOrigin } from "@needl-ai/common/utils/requestOrigin";

import type { RateLimitInfo } from "@/types/chat";
import type { SSEOptions, SSEvent } from "sse.js";

const EVENTS = {
  START: "[START]",
  ERROR: "[ERROR]",
};

export const EVENTS_STATUS = {
  KEEP_ALIVE: "KEEP_ALIVE",
  MESSAGE_ID: "MESSAGE_ID",
  TOOL_CALL_INITIATED: "TOOL_CALL_INITIATED",
  TOOL_CALL_ARGUMENTS: "TOOL_CALL_ARGUMENTS",
  TOOL_CALL_FINISHED: "TOOL_CALL_FINISHED",
  REASONING_SUMMARY: "REASONING_SUMMARY",
  TEXT_DELTA: "TEXT_DELTA",
  GENERATING_CITATIONS: "GENERATING_CITATIONS",
  INTERMEDIATE_ERROR: "INTERMEDIATE_ERROR",
  ERROR: "ERROR",
  DONE: "DONE",
};

const reducer = <T>(
  state: SSEStateType<T>,
  action: SSEActionType<T>
): SSEStateType<T> => {
  switch (action.type) {
    case "SET_LOADING":
      return {
        ...state,
        isLoading: action.payload,
        isStreaming: !action.payload,
        isError: false,
      };
    case "SET_ERROR":
      return {
        ...state,
        isLoading: false,
        isStreaming: false,
        isError: true,
        errorSource: action.payload,
      };
    case "EMIT_EVENT":
      return {
        ...state,
        events: [...state.events, action.payload],
      };
    case "SET_DATA":
      return {
        ...state,
        isLoading: false,
        isStreaming: false,
        data: action.payload,
      };
    case "SET_RATE_LIMIT_INFO":
      return {
        ...state,
        rateLimitInfo: action.payload,
      };
    case "CLEAR_DATA":
      return {
        ...state,
        events: [],
        data: undefined,
        isLoading: false,
        isStreaming: false,
        isError: false,
        rateLimitInfo: undefined,
        errorSource: undefined,
      };
    default:
      throw new Error("Invalid action type for useSSERequest reducer");
  }
};

export const useSSERequest = <
  ResponseType,
  ContentEventType = ContentEvent<string>,
>(
  rawEvents: boolean = true
) => {
  const [state, dispatch] = React.useReducer(reducer<ResponseType>, {
    isLoading: false,
    isStreaming: false,
    isError: false,
    events: [],
    data: undefined,
    rateLimitInfo: undefined,
    errorSource: undefined,
  });

  const isLoadingRef = React.useRef(state.isLoading);

  const setLoading = React.useCallback((loading: boolean) => {
    isLoadingRef.current = loading;
    dispatch({ type: "SET_LOADING", payload: loading });
  }, []);

  const makeSSERequest = React.useCallback(
    async (
      url: string,
      options: SSEOptions,
      stopLoadingEventStatus: string,
      contentEventHandler: (event: ContentEventType) => void
    ) => {
      setLoading(true);

      const eventSource = new SSE(url, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          ...options.headers,
          "x-request-origin": getRequestOrigin(),
        },
      });

      eventSource.onmessage = (event) => {
        if (rawEvents) {
          dispatch({ type: "EMIT_EVENT", payload: event });
        }

        if (event.data && event.data.startsWith(EVENTS.ERROR)) {
          dispatch({ type: "SET_ERROR", payload: "sse_event" });
        }

        if (event.data && !event.data.startsWith("[")) {
          let response;
          try {
            response = JSON.parse(event.data);
          } catch (parseError) {
            console.error("Error parsing SSE response:", parseError);
            // dispatch({ type: "SET_ERROR" });
            toast({
              variant: "destructive",
              title: "Response Parse Error",
              description: "Failed to parse server response. Please try again.",
              duration: 10000,
            });
            return;
          }

          if (
            // response.status === stopLoadingEventStatus &&
            isLoadingRef.current
          ) {
            setLoading(false);
          }

          if (response.status === EVENTS_STATUS.DONE) {
            dispatch({
              type: "SET_DATA",
              payload: response.data as ResponseType,
            });

            if (response.data?.rateLimitInfo) {
              dispatch({
                type: "SET_RATE_LIMIT_INFO",
                payload: response.data.rateLimitInfo as RateLimitInfo,
              });
            }

            contentEventHandler(response);
          } else if (response.status === EVENTS_STATUS.INTERMEDIATE_ERROR) {
            console.error(response);
            toast({
              variant: "destructive",
              title: "Parsing Chunk Error",
              description:
                response.data?.error ||
                "Something went wrong! Please try again later.",
              duration: 10000,
            });
          } else if (response.status === EVENTS_STATUS.ERROR) {
            console.error(response);
            dispatch({ type: "SET_ERROR", payload: "sse_event" });

            if (response.data?.rateLimitInfo) {
              dispatch({
                type: "SET_RATE_LIMIT_INFO",
                payload: response.data.rateLimitInfo as RateLimitInfo,
              });
            }

            contentEventHandler(response);

            toast({
              variant: "destructive",
              title: "Streaming Error",
              description: response.data?.error
                ? `${response.data?.origin === "node_express" ? "Node: " : "ML: "} ${response.data?.error}`
                : "Something went wrong! Please try again later.",
              duration: 10000,
            });
          } else {
            contentEventHandler(response);
          }
        }
      };

      eventSource.onerror = (error) => {
        eventSource.close();
        dispatch({ type: "SET_ERROR", payload: "connection" });
        console.error("SSE error:", error);

        contentEventHandler({
          status: EVENTS_STATUS.ERROR,
          data: {
            error: "The connection to the server was lost. Please try again.",
          },
        } as ContentEventType);

        toast({
          variant: "destructive",
          title: "Connection Error",
          description:
            "The connection to the server was lost. Please try again.",
          duration: 10000,
        });
      };

      eventSource.onopen = () => {
        console.log("SSE connection established.");
      };

      return () => {
        if (eventSource) {
          eventSource.close();

          console.log("SSE connection closed.");
        }
      };
    },
    [rawEvents]
  );

  const clearData = React.useCallback(() => {
    dispatch({ type: "CLEAR_DATA" });
  }, []);

  return {
    makeSSERequest,
    clearData,
    setLoading,
    isLoading: state.isLoading,
    isStreaming: state.isStreaming,
    isError: state.isError,
    errorSource: state.errorSource,
    events: state.events,
    data: state.data,
    rateLimitInfo: state.rateLimitInfo,
  };
};

type ErrorSource = "sse_event" | "connection";

type SSEStateType<T> = {
  events: SSEvent[];
  data: T | undefined;
  isLoading: boolean;
  isStreaming: boolean;
  isError: boolean;
  errorSource: ErrorSource | undefined;
  rateLimitInfo: RateLimitInfo | undefined;
};

type SSEActionType<T> =
  | {
      type: "CLEAR_DATA";
    }
  | {
      type: "EMIT_EVENT";
      payload: SSEvent;
    }
  | {
      type: "SET_DATA";
      payload: T;
    }
  | {
      type: "SET_ERROR";
      payload?: ErrorSource;
    }
  | {
      type: "SET_LOADING";
      payload: boolean;
    }
  | {
      type: "SET_RATE_LIMIT_INFO";
      payload: RateLimitInfo;
    };

export type ContentEvent<T> = {
  status: string;
  data: T;
};
